export interface ValidationResult {
  isValid: boolean;
  error?: string; // Should be a translation key in the actual app
  correctedValue?: string;
}

export const validateURL = (url: string): ValidationResult => {
  let correctedValue = url.trim();
  if (!correctedValue) return { isValid: false, error: 'validation.url.empty' };
  
  if (!/^https?:\/\//i.test(correctedValue)) {
    correctedValue = `https://${correctedValue}`;
  }

  if (correctedValue.length > 2048) {
    return { isValid: false, error: 'validation.url.tooLong' };
  }

  const urlRegex = /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_.+~#?&//=]*)$/i;
  if (!urlRegex.test(correctedValue)) {
    return { isValid: false, error: 'validation.url.invalid' };
  }

  try {
    new URL(correctedValue);
    return { isValid: true, correctedValue };
  } catch {
    return { isValid: false, error: 'validation.url.invalid' };
  }
};

export const validateText = (text: string): ValidationResult => {
  if (!text.trim()) return { isValid: false, error: 'validation.text.empty' };
  if (text.length > 2000) return { isValid: false, error: 'validation.text.tooLong' };
  return { isValid: true };
};

export const validateEmail = (email: string): ValidationResult => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!email.trim() || !emailRegex.test(email)) {
    return { isValid: false, error: 'validation.email.invalid' };
  }
  if (email.length > 254) return { isValid: false, error: 'validation.email.tooLong' };
  return { isValid: true };
};

export const validatePhone = (phone: string): ValidationResult => {
  const correctedValue = phone.replace(/[\s\-()]/g, '');
  const phoneRegex = /^\+\d{7,15}$/;
  if (!phoneRegex.test(correctedValue)) {
    return { isValid: false, error: 'validation.phone.invalid', correctedValue };
  }
  return { isValid: true, correctedValue };
};

export const validateWifi = (ssid: string, security: string, password?: string): ValidationResult => {
  if (!ssid.trim() || ssid.length > 32) return { isValid: false, error: 'validation.wifi.ssid' };
  if (security !== 'nopass') {
    if (!password) return { isValid: false, error: 'validation.wifi.passwordRequired' };
    if (security === 'WPA' && (password.length < 8 || password.length > 63)) {
      return { isValid: false, error: 'validation.wifi.wpaLength' };
    }
  }
  return { isValid: true };
};

export const validateVCard = (fn: string): ValidationResult => {
  if (!fn.trim()) return { isValid: false, error: 'validation.vcard.fnRequired' };
  return { isValid: true };
};

export const validateCalendar = (title: string, start: string, end?: string): ValidationResult => {
  if (!title.trim()) return { isValid: false, error: 'validation.calendar.titleRequired' };
  if (!start) return { isValid: false, error: 'validation.calendar.startRequired' };
  
  const startTime = new Date(start).getTime();
  if (isNaN(startTime)) return { isValid: false, error: 'validation.calendar.invalidStart' };

  if (end) {
    const endTime = new Date(end).getTime();
    if (isNaN(endTime)) return { isValid: false, error: 'validation.calendar.invalidEnd' };
    if (endTime < startTime) return { isValid: false, error: 'validation.calendar.endBeforeStart' };
  }
  return { isValid: true };
};

export const validateGeo = (lat?: string | number | null, lng?: string | number | null): ValidationResult => {
  const latStr = String(lat ?? '').trim();
  const lngStr = String(lng ?? '').trim();

  if (latStr === '') return { isValid: false, error: 'validation.geo.lat' };
  if (lngStr === '') return { isValid: false, error: 'validation.geo.lng' };

  const latitude = Number(latStr);
  const longitude = Number(lngStr);
  if (isNaN(latitude) || latitude < -90 || latitude > 90) return { isValid: false, error: 'validation.geo.lat' };
  if (isNaN(longitude) || longitude < -180 || longitude > 180) return { isValid: false, error: 'validation.geo.lng' };
  
  return { 
    isValid: true, 
    correctedValue: `${parseFloat(latitude.toFixed(6))},${parseFloat(longitude.toFixed(6))}`
  };
};
